// Centralized error handling utility
export class NetworkErrorHandler {
  static logError(error, context = '') {
    console.group(`🚨 Network Error - ${context}`);
    console.error('Error details:', error);
    console.error('Error message:', error.message);
    console.error('Error code:', error.code);
    console.error('Error response:', error.response);
    console.error('Error config:', error.config);
    console.groupEnd();
  }

  static isNetworkError(error) {
    return error.message === 'Network Error' || 
           error.code === 'NETWORK_ERROR' ||
           error.name === 'AxiosError';
  }

  static getErrorMessage(error) {
    if (this.isNetworkError(error)) {
      return 'Erro de conexão. Verifique sua internet e tente novamente.';
    }
    
    if (error.response?.status === 401) {
      return 'Sessão expirada. Faça login novamente.';
    }
    
    if (error.response?.status >= 500) {
      return 'Erro do servidor. Tente novamente em alguns minutos.';
    }
    
    return error.message || 'Erro desconhecido';
  }

  static async handleEntityError(error, entityName, operation) {
    this.logError(error, `${entityName}.${operation}`);
    
    if (this.isNetworkError(error)) {
      // Try to detect if it's an authentication issue
      try {
        const { User } = await import('@/entities/User');
        await User.me();
      } catch (authError) {
        console.warn('Authentication check failed:', authError);
        // Could redirect to login here if needed
      }
    }
    
    throw error; // Re-throw so components can handle
  }
}

// Wrapper for entity calls with error handling
export const safeEntityCall = async (entityCall, entityName, operation) => {
  try {
    return await entityCall();
  } catch (error) {
    await NetworkErrorHandler.handleEntityError(error, entityName, operation);
  }
};

// Generic error type for better type safety
interface GenericError {
  message: string;
  code?: string;
  response?: any;
  name?: string;
}

// Utility for handling async operations with loading states
export const withAsyncHandler = <T extends any[], R>(
  asyncFn: (...args: T) => Promise<R>,
  context?: string,
  operation?: string,
  setLoading?: (loading: boolean) => void,
  setError?: (error: string | null) => void
) => {
  return async (...args: T): Promise<R | null> => {
    try {
      setLoading?.(true);
      setError?.(null);
      const result = await asyncFn(...args);
      return result;
    } catch (error) {
      const errorMessage = NetworkErrorHandler.getErrorMessage(error as GenericError);
      setError?.(errorMessage);
      if (context && operation) {
        await NetworkErrorHandler.handleEntityError(error as GenericError, context, operation);
      }
      return null;
    } finally {
      setLoading?.(false);
    }
  };
};
